import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api.js';
import { extractApiError } from '../../utils/pdfDownload';
import { formatDocumentToast } from '../../utils/documentToast';
import type { AttachedDocumentRef } from '../../utils/documentToast';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import {
  countDocumentGrain,
  groupOrdersByPos,
  mergeOpenOrders,
  selectedOrders,
  uniquePosCount,
  pickableOrders,
  type OpenOwnOrder,
} from '../../utils/pickListGrouping';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { PickListOrderSelect } from './PickListOrderSelect';
import { PickListPackOverview } from './PickListPackOverview';
import {
  EndPlaceSelect,
  readCurrentLocation,
} from '../delivery/PlacePicker';
import type { GeoPoint } from '../../types/deliveryTour';

type Step = 1 | 2 | 3;

interface CreatePickListSheetProps {
  open: boolean;
  listId: string | null;
  onClose: () => void;
  onConfirmed: (documents: AttachedDocumentRef[], batchId?: string | null) => void;
}

function toggleId(current: Set<string>, id: string): Set<string> {
  const next = new Set(current);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

function toggleGroup(current: Set<string>, orders: OpenOwnOrder[]): Set<string> {
  const next = new Set(current);
  const allOn = orders.every((o) => next.has(o.id));
  for (const order of orders) {
    if (allOn) next.delete(order.id);
    else next.add(order.id);
  }
  return next;
}

export const CreatePickListSheet: React.FC<CreatePickListSheetProps> = ({
  open,
  listId,
  onClose,
  onConfirmed,
}) => {
  const { isOnline } = useOnlineStatus();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packedId, setPackedId] = useState<string | null>(null);
  const [orders, setOrders] = useState<OpenOwnOrder[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createBatch, setCreateBatch] = useState(false);
  const [start, setStart] = useState<GeoPoint | null>(null);
  const [end, setEnd] = useState<GeoPoint | null>(null);
  const [locating, setLocating] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const openRes = await api.get('/orders/open-own');
      const openRows = pickableOrders((openRes.data || []) as OpenOwnOrder[]);
      if (listId) {
        const listRes = await api.get(`/pick-lists/${listId}`);
        const listOrders = (listRes.data?.orders || []) as OpenOwnOrder[];
        setOrders(mergeOpenOrders(listOrders, openRows));
        setSelectedIds(new Set(listOrders.map((o) => o.id)));
        setPackedId(listId);
        setStep(2);
      } else {
        setOrders(openRows);
        setSelectedIds(new Set(openRows.map((o) => o.id)));
        setPackedId(null);
        setStep(1);
      }
      setCreateBatch(false);
      setStart(null);
      setEnd(null);
    } catch (err) {
      toast.error(extractApiError(err).message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const chosen = useMemo(
    () => selectedOrders(orders, selectedIds),
    [orders, selectedIds]
  );
  const groups = useMemo(() => groupOrdersByPos(orders), [orders]);
  const grain = countDocumentGrain(chosen);

  const requestGps = async () => {
    setLocating(true);
    try {
      setStart(await readCurrentLocation());
    } catch (err) {
      setStart(null);
      toast.error(err instanceof Error ? err.message : 'Could not read GPS');
    } finally {
      setLocating(false);
    }
  };

  const onToggleBatch = (value: boolean) => {
    setCreateBatch(value);
    if (value) void requestGps();
    else setStart(null);
  };

  const persist = async (): Promise<string | null> => {
    if (!isOnline) {
      toast.error('Pick lists need a network connection');
      return null;
    }
    if (chosen.length === 0) {
      toast.error('Select at least one order');
      return null;
    }
    setSaving(true);
    try {
      const body = { order_ids: chosen.map((o) => o.id) };
      if (packedId) {
        await api.patch(`/pick-lists/${packedId}`, body);
        return packedId;
      }
      const res = await api.post('/pick-lists', body);
      const id = res.data.id as string;
      setPackedId(id);
      return id;
    } catch (err) {
      toast.error(extractApiError(err).message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const goPack = async () => {
    const id = await persist();
    if (id) setStep(2);
  };

  const goBackSelect = async () => {
    const id = await persist();
    if (id) setStep(1);
  };

  const confirmPick = async () => {
    if (!isOnline) {
      toast.error('Pick lists need a network connection');
      return;
    }
    const id = await persist();
    if (!id) return;
    setSaving(true);
    try {
      const res = await api.post(`/pick-lists/${id}/confirm`, {
        create_delivery_batch: createBatch,
        start: createBatch ? start : undefined,
        end: createBatch && end ? end : undefined,
      });
      const documents = (res.data?.documents || []) as AttachedDocumentRef[];
      toast.success(
        formatDocumentToast(documents) || 'Draft delivery notes created'
      );
      if (res.data?.skipped_ungeocoded?.length) {
        toast.warning(
          `Not on the tour (no coordinates); packed lines are back in Own Delivery: ${res.data.skipped_ungeocoded.join(', ')}`
        );
      }
      if (res.data?.skipped_leftover?.length) {
        toast.warning(
          `Left off the tour; packed lines are back in Own Delivery: ${res.data.skipped_leftover.join(', ')}`
        );
      }
      if (res.data?.batch_skipped_reason) {
        toast.warning(res.data.batch_skipped_reason);
      } else if (res.data?.delivery_batch_id) {
        toast.success('Delivery tour created');
      }
      onConfirmed(documents, res.data?.delivery_batch_id || null);
      onClose();
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const cancelList = async () => {
    if (!packedId) {
      onClose();
      return;
    }
    try {
      await api.post(`/pick-lists/${packedId}/cancel`);
      toast.success('Pick list cancelled — orders are open again');
      onClose();
    } catch (err) {
      toast.error(extractApiError(err).message);
    }
  };

  const title =
    step === 1 ? 'Select orders' : step === 2 ? 'Pack' : 'Confirm pick';

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="fixed inset-0 z-50 bg-white flex flex-col"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div>
              <Dialog.Title className="text-lg font-black text-gray-900">
                {title}
              </Dialog.Title>
              <p className="text-xs font-bold text-gray-400">
                Step {step} of 3
              </p>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="p-2 text-gray-400" aria-label="Close">
                <X size={22} />
              </button>
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {loading ? (
              <p className="text-center text-gray-400 font-bold py-20">Loading...</p>
            ) : null}
            {!loading && step === 1 ? (
              <PickListOrderSelect
                groups={groups}
                selectedIds={selectedIds}
                onToggle={(id) => setSelectedIds((cur) => toggleId(cur, id))}
                onToggleGroup={(rows) =>
                  setSelectedIds((cur) => toggleGroup(cur, rows))
                }
              />
            ) : null}
            {!loading && step === 2 ? (
              <PickListPackOverview orders={chosen} />
            ) : null}
            {!loading && step === 3 ? (
              <ConfirmStep
                grain={grain}
                createBatch={createBatch}
                onToggleBatch={onToggleBatch}
                start={start}
                locating={locating}
                end={end}
                onEnd={setEnd}
              />
            ) : null}
          </div>
          {!loading ? (
            <SheetFooter
              step={step}
              saving={saving}
              lineCount={chosen.length}
              storeCount={uniquePosCount(chosen)}
              packedId={packedId}
              onContinue={goPack}
              onBack={goBackSelect}
              onToConfirm={() => setStep(3)}
              onBackPack={() => setStep(2)}
              onConfirm={confirmPick}
              canConfirm={!createBatch || Boolean(start)}
              onAskCancel={() => setCancelOpen(true)}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel pick list?"
        message="Selected orders will show up on Own delivery again for everyone."
        confirmText="Cancel pick list"
        variant="danger"
        onConfirm={cancelList}
      />
    </Dialog.Root>
  );
};

function ConfirmStep({
  grain,
  createBatch,
  onToggleBatch,
  start,
  locating,
  end,
  onEnd,
}: {
  grain: number;
  createBatch: boolean;
  onToggleBatch: (value: boolean) => void;
  start: GeoPoint | null;
  locating: boolean;
  end: GeoPoint | null;
  onEnd: (point: GeoPoint | null) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-base font-black text-gray-900">
        Create {grain} delivery note{grain === 1 ? '' : 's'}
      </p>
      <p className="text-sm text-gray-600">
        Draft Lieferscheine (one per store × manufacturer) will appear on
        Delivery notes.
      </p>
      <label className="flex items-start gap-3 bg-gray-50 rounded-2xl p-4">
        <input
          type="checkbox"
          checked={createBatch}
          onChange={(e) => onToggleBatch(e.target.checked)}
          className="mt-1 h-5 w-5 accent-brand-600"
        />
        <span>
          <span className="font-black text-gray-900">Also create a delivery tour</span>
          <span className="block text-xs text-gray-500 mt-1">
            Starts from your location. Optional saved place as the finish.
          </span>
        </span>
      </label>
      {createBatch ? (
        <div className="space-y-4">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-gray-400">
              Start
            </p>
            <p className="text-sm font-bold text-gray-900 mt-1">
              {locating
                ? 'Reading GPS…'
                : start
                  ? 'Your location'
                  : 'GPS needed to start the tour'}
            </p>
          </div>
          <EndPlaceSelect value={end} onChange={onEnd} />
        </div>
      ) : null}
    </div>
  );
}

function SheetFooter({
  step,
  saving,
  lineCount,
  storeCount,
  packedId,
  onContinue,
  onBack,
  onToConfirm,
  onBackPack,
  onConfirm,
  onAskCancel,
  canConfirm,
}: {
  step: Step;
  saving: boolean;
  lineCount: number;
  storeCount: number;
  packedId: string | null;
  onContinue: () => void;
  onBack: () => void;
  onToConfirm: () => void;
  onBackPack: () => void;
  onConfirm: () => void;
  onAskCancel: () => void;
  canConfirm: boolean;
}) {
  return (
    <div className="border-t bg-white px-4 py-3 space-y-2">
      {step === 1 ? (
        <button
          type="button"
          disabled={saving || lineCount === 0}
          onClick={onContinue}
          className="w-full min-h-[48px] rounded-xl bg-brand-600 text-white font-black disabled:opacity-50"
        >
          {lineCount} lines · {storeCount} stores → Continue
        </button>
      ) : null}
      {step === 2 ? (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onBack}
            className="flex-1 min-h-[48px] rounded-xl bg-gray-100 font-bold"
          >
            Back
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onToConfirm}
            className="flex-[2] min-h-[48px] rounded-xl bg-brand-600 text-white font-black"
          >
            Continue
          </button>
        </div>
      ) : null}
      {step === 3 ? (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onBackPack}
            className="flex-1 min-h-[48px] rounded-xl bg-gray-100 font-bold"
          >
            Back
          </button>
          <button
            type="button"
            disabled={saving || !canConfirm}
            onClick={onConfirm}
            className="flex-[2] min-h-[48px] rounded-xl bg-brand-600 text-white font-black disabled:opacity-50"
          >
            Confirm pick
          </button>
        </div>
      ) : null}
      {packedId ? (
        <button
          type="button"
          onClick={onAskCancel}
          className="w-full text-xs font-bold text-red-600 py-1"
        >
          Cancel pick list
        </button>
      ) : null}
    </div>
  );
}
